import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Activity, MapPin, Phone, Stethoscope, Calendar, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function PublicDoctorPage() {
  const { slug } = useParams();
  const [site, setSite] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", preferred_date: "", reason: "", notes: "" });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Loading… — Doctor";
    axios.get(`${API}/public/tenants/${slug}`)
      .then((r) => {
        setSite(r.data);
        document.title = `${r.data.name} — Book online`;
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Drop empty optional fields so the backend's pydantic EmailStr doesn't reject ""
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== "" && v != null)
      );
      await axios.post(`${API}/public/tenants/${slug}/inquiry`, payload);
      setSent(true);
      toast.success("Request received");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send");
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <div className="text-center space-y-3">
          <div className="size-12 mx-auto rounded-full bg-muted grid place-items-center">
            <Stethoscope className="size-6 text-muted-foreground" />
          </div>
          <div className="font-semibold text-xl">Practice not found</div>
          <p className="text-sm text-muted-foreground">The address you entered doesn't match any clinic on Senrize Medical Flow.</p>
        </div>
      </div>
    );
  }
  if (!site) {
    return <div className="min-h-screen grid place-items-center bg-background text-sm text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <div className="bg-primary text-primary-foreground dotted-bg">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12 flex flex-col md:flex-row gap-8 md:items-center">
          <Avatar className="size-32 md:size-40 ring-4 ring-primary-foreground/20">
            {site.photo_url && <AvatarImage src={site.photo_url} alt={site.name} />}
            <AvatarFallback className="bg-primary-foreground/20 text-3xl font-bold">
              {site.name?.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 fade-up">
            <div className="text-[11px] uppercase tracking-[0.25em] opacity-80">Private practice</div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mt-2">{site.name}</h1>
            <div className="flex flex-wrap gap-2 mt-3">
              {site.specialty && <Badge variant="secondary" className="bg-primary-foreground/15 text-primary-foreground border-0">{site.specialty}</Badge>}
              {site.location && <Badge variant="secondary" className="bg-primary-foreground/15 text-primary-foreground border-0">{site.location.split(",")[0]}</Badge>}
            </div>
            {site.bio && <p className="mt-5 text-base leading-relaxed max-w-2xl opacity-95">{site.bio}</p>}
            <a href="#book" className="inline-block mt-7">
              <Button size="lg" variant="secondary" className="gap-2" data-testid="hero-book-btn">
                <Calendar className="size-4" /> Book an appointment
              </Button>
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Services + contact */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardContent className="p-6">
              <h2 className="text-2xl font-semibold tracking-tight">Services</h2>
              <p className="text-sm text-muted-foreground mt-1">Available treatments & consultations</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-5">
                {(site.services || []).map((s) => (
                  <div key={s} className="flex items-center gap-2.5 p-3 border border-border rounded-lg" data-testid={`service-${s.toLowerCase().replace(/\s+/g, "-")}`}>
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span className="text-sm">{s}</span>
                  </div>
                ))}
                {(!site.services || !site.services.length) && (
                  <div className="text-sm text-muted-foreground">Services coming soon.</div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {site.location && (
                <div className="flex items-start gap-3">
                  <MapPin className="size-5 text-primary mt-0.5" />
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Location</div>
                    <div className="text-sm font-medium mt-0.5">{site.location}</div>
                  </div>
                </div>
              )}
              {site.phone && (
                <div className="flex items-start gap-3">
                  <Phone className="size-5 text-primary mt-0.5" />
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Phone</div>
                    <div className="text-sm font-medium mt-0.5 mono">{site.phone}</div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Booking form */}
        <div id="book" className="lg:col-span-1">
          <Card className="lg:sticky lg:top-6">
            <CardContent className="p-6">
              {sent ? (
                <div className="text-center py-8 space-y-3" data-testid="inquiry-sent">
                  <div className="size-12 mx-auto rounded-full bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] grid place-items-center">
                    <CheckCircle2 className="size-6" />
                  </div>
                  <div className="font-semibold text-lg">Thanks, we've got it.</div>
                  <p className="text-sm text-muted-foreground">{site.name} will contact you shortly to confirm your appointment.</p>
                  <Button variant="outline" onClick={() => { setSent(false); setForm({ name: "", phone: "", email: "", preferred_date: "", reason: "", notes: "" }); }}>
                    Send another
                  </Button>
                </div>
              ) : (
                <>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Book</div>
                  <h3 className="text-xl font-semibold mt-1">Request an appointment</h3>
                  <p className="text-sm text-muted-foreground mt-1">We'll get back to you within 24h.</p>
                  <form onSubmit={submit} className="space-y-3 mt-5" data-testid="inquiry-form">
                    <div className="space-y-1.5"><Label>Full name *</Label>
                      <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="inquiry-name" /></div>
                    <div className="space-y-1.5"><Label>Phone *</Label>
                      <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="inquiry-phone" /></div>
                    <div className="space-y-1.5"><Label>Email</Label>
                      <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="inquiry-email" /></div>
                    <div className="space-y-1.5"><Label>Preferred date</Label>
                      <Input type="datetime-local" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Reason</Label>
                      <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Consultation, follow-up..." /></div>
                    <Button type="submit" disabled={submitting} className="w-full" data-testid="inquiry-submit">
                      {submitting ? "Sending..." : "Send request"}
                    </Button>
                    <p className="text-[11px] text-muted-foreground text-center">
                      Already a patient? <a href="/login" className="text-primary hover:underline">Sign in</a>
                    </p>
                  </form>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <footer className="border-t border-border py-6 mt-8">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <Activity className="size-3.5 text-primary" />
            <span>Powered by Senrize Medical Flow</span>
          </div>
          <div>© {new Date().getFullYear()} {site.name}</div>
        </div>
      </footer>
    </div>
  );
}
